/**
 * MultiStandardPreviewModal · Sprint P37c · Feb 2026
 *
 * Opens a modal that renders one PDF per reporting standard (JORC 2012,
 * NI 43-101, SEC S-K 1300, UNFC, Internal) in parallel and shows a
 * table of size + page count + preview + download.
 *
 * Uses the project's persisted branding but overrides the standard
 * per row so the user can see how the same data appears under each
 * regulatory framework in one place.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, FilePdf, Eye, DownloadSimple, ArrowClockwise, CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";


const STANDARDS = [
  { key: "JORC_2012",   label: "JORC 2012",           tone: "sky",     report_type: "CPR" },
  { key: "NI_43_101",   label: "NI 43-101 / CIM",     tone: "indigo",  report_type: "CPR" },
  { key: "SEC_SK_1300", label: "SEC S-K 1300 TRS",    tone: "violet",  report_type: "CPR" },
  { key: "UNFC",        label: "UNFC (E/F/G)",        tone: "emerald", report_type: "MRE" },
  { key: "INTERNAL",    label: "Internal (Not Public)", tone: "slate", report_type: "INTERNAL" },
];


export default function MultiStandardPreviewModal({ open, onClose, projectId, project }) {
  const [rows, setRows] = useState(() =>
    STANDARDS.map((s) => ({ ...s, status: "idle", bytes: 0, elapsed: 0,
                              blob: null, error: "" }))
  );
  const [running, setRunning] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setRows(STANDARDS.map((s) => ({ ...s, status: "idle", bytes: 0,
                                          elapsed: 0, blob: null, error: "" })));
    }
  }, [open]);

  const runAll = async () => {
    setRunning(true);
    const start = performance.now();
    // Fire all five in parallel; report each as it lands.
    await Promise.allSettled(STANDARDS.map((s, i) => generateOne(s, i)));
    setRunning(false);
    const total = ((performance.now() - start) / 1000).toFixed(1);
    toast.success(`All standards rendered in ${total}s`);
  };

  const generateOne = async (s, idx) => {
    setRows((r) => r.map((row, i) => i === idx ? {
      ...row, status: "running", bytes: 0, elapsed: 0, blob: null, error: ""
    } : row));
    const t0 = performance.now();
    try {
      const res = await api.get(
        `/projects/${projectId}/export/pro-cpr.pdf`,
        {
          params: {
            status: "DRAFT",
            report_type: s.report_type,
            primary_standard: s.key,
            wait_for_narratives: false,
            llm_chapters: false,
          },
          responseType: "blob",
          timeout: 90_000,
        }
      );
      const dt = (performance.now() - t0) / 1000;
      setRows((r) => r.map((row, i) => i === idx ? {
        ...row, status: "done", bytes: res.data.size,
        elapsed: dt, blob: res.data
      } : row));
    } catch (err) {
      const dt = (performance.now() - t0) / 1000;
      setRows((r) => r.map((row, i) => i === idx ? {
        ...row, status: "failed", elapsed: dt,
        error: err?.response?.data?.detail || err.message || "network"
      } : row));
    }
  };

  const preview = (row) => {
    if (!row.blob) return;
    const url = URL.createObjectURL(row.blob);
    window.open(url, "_blank", "noopener");
  };

  const download = (row) => {
    if (!row.blob) return;
    const url = URL.createObjectURL(row.blob);
    const a = document.createElement("a"); a.href = url;
    const name = (project?.name || "Project").replace(/\s+/g, "_");
    a.download = `${name}_${row.key}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    const done   = rows.filter((r) => r.status === "done").length;
    const failed = rows.filter((r) => r.status === "failed").length;
    return { done, failed, total: rows.length };
  }, [rows]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
         onClick={onClose}
         data-testid="ms-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-sky-50 to-white">
          <FilePdf size={20} weight="fill" className="text-brand-blue" />
          <div className="flex-1">
            <div className="text-[15px] font-bold text-slate-800">Multi-Standard PDF Preview</div>
            <div className="text-[11px] text-slate-500">
              Renders one report per reporting framework side-by-side ·
              same project data · different terminology, cover title and
              chapter structure
            </div>
          </div>
          <Button onClick={onClose} variant="ghost" size="sm"
                  className="h-8 w-8 p-0" data-testid="ms-preview-close">
            <X size={16} />
          </Button>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[12px] text-slate-600">
              Progress: <b>{summary.done}</b> done · <b>{summary.failed}</b> failed
              · <b>{summary.total - summary.done - summary.failed}</b> remaining
            </div>
            <Button onClick={runAll} disabled={running}
                    className="h-8 text-[12px]"
                    data-testid="ms-preview-run">
              {running ? (<><ArrowClockwise size={13} className="mr-1 animate-spin" />Rendering…</>)
                          : "Render all 5 standards"}
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Standard</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-3 py-2 font-semibold">Size</th>
                  <th className="text-right px-3 py-2 font-semibold">Elapsed</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className="border-t border-slate-100"
                      data-testid={`ms-preview-row-${row.key}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {row.label}
                      <div className="text-[10px] text-slate-500">{row.report_type}</div>
                    </td>
                    <td className="px-3 py-2">
                      <StatusChip status={row.status} error={row.error} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {row.bytes ? `${(row.bytes / 1024 / 1024).toFixed(2)} MB` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                      {row.elapsed ? `${row.elapsed.toFixed(1)}s` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="sm"
                                onClick={() => preview(row)}
                                disabled={row.status !== "done"}
                                className="h-6 px-2 text-[11px]"
                                data-testid={`ms-preview-view-${row.key}`}>
                          <Eye size={11} className="mr-1" /> View
                        </Button>
                        <Button variant="ghost" size="sm"
                                onClick={() => download(row)}
                                disabled={row.status !== "done"}
                                className="h-6 px-2 text-[11px]"
                                data-testid={`ms-preview-download-${row.key}`}>
                          <DownloadSimple size={11} className="mr-1" /> Save
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10.5px] text-slate-500 italic">
            Every report uses the same project data + persisted branding.
            The only variables between rows are the report type and the
            regulatory framework. Terminology (CP vs QP, Ore vs Mineral
            Reserve, Proved vs Proven), chapter structure, cover title
            and declaration profile all switch automatically.
          </div>
        </div>
      </div>
    </div>
  );
}


function StatusChip({ status, error }) {
  if (status === "idle") return (
    <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
      Pending
    </span>
  );
  if (status === "running") return (
    <span className="inline-flex items-center gap-1 text-[10.5px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
      <ArrowClockwise size={10} className="animate-spin" /> Rendering
    </span>
  );
  if (status === "done") return (
    <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
      <CheckCircle size={10} weight="fill" /> Ready
    </span>
  );
  return (
    <span title={error} className="inline-flex items-center gap-1 text-[10.5px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">
      <XCircle size={10} weight="fill" /> Failed
    </span>
  );
}
